/* @jsxImportSource react */
'use client';

import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';

const TERMS_URL = 'https://www.opendoor.com/w/terms';
const PRIVACY_URL = 'https://www.opendoor.com/w/privacy';

export interface ConsentDisclaimerProps {
  /**
   * `APP-COMPONENT` of the owning surface — the two links append `-terms` and
   * `-privacy-policy`. Required: the mounting module knows its own component
   * segment, and a default here could only guess it wrong.
   */
  analyticsPrefix: string;
  className?: string;
}

/**
 * Consent line for the screens whose CTA creates an account. Copy is fixed here
 * rather than exposed as a prop — it is the flow's legal posture. `RegistrationModule`
 * takes an override node; the entry screen renders this unconditionally.
 *
 * "By continuing" names no button on purpose: the entry screen creates accounts
 * from three CTAs and none of them reads "Continue", so quoting one would
 * attribute consent to a control the user may not have pressed.
 *
 * The `aria-label`s lead with the visible text because an `aria-label` replaces
 * the accessible name outright — a bare description would drop the visible text
 * from it, failing WCAG 2.5.3 (Label in Name) and leaving voice control nothing
 * to match.
 */
export const ConsentDisclaimer = ({ analyticsPrefix, className }: ConsentDisclaimerProps) => {
  return (
    <Text as="p" className={cn('text-body-sm text-od-content-secondary', className)}>
      By continuing, I agree to Opendoor&rsquo;s{' '}
      <Link
        analyticsName={`${analyticsPrefix}-terms`}
        href={TERMS_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Terms of Service, opens a new page"
      >
        Terms of Service
      </Link>{' '}
      and{' '}
      <Link
        analyticsName={`${analyticsPrefix}-privacy-policy`}
        href={PRIVACY_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Privacy Policy, opens a new page"
      >
        Privacy Policy
      </Link>
      .
    </Text>
  );
};

ConsentDisclaimer.displayName = 'ConsentDisclaimer';
